package com.mps.asc_app.data.auth

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.AuthResult
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

data class AuthUser(
    val uid: String,
    val email: String?,
)

class AuthRepositoryImpl: AuthRepository {
    private val auth = Firebase.auth

    override suspend fun login(email: String, password: String) : Result<AuthUser> =
        runCatching {
            val result: AuthResult = auth.signInWithEmailAndPassword(email, password)
            val user = result.user ?: throw IllegalStateException("Kein Benutzer zurückgegeben")
            user.toAuthUser()
        }

    override suspend fun logout(): Result<Unit> =
        runCatching {
            auth.signOut()
        }

    override fun isLoggedIn(): Boolean = auth.currentUser != null

    override fun currentUser(): AuthUser? = auth.currentUser?.toAuthUser()

    override val authState: Flow<AuthUser?> =
        auth.authStateChanged.map { firebaseUser: FirebaseUser? ->
            firebaseUser?.toAuthUser()
        }

    override suspend fun register(
        email: String,
        password: String
    ): Result<AuthUser> =
        runCatching {
            val result = auth.createUserWithEmailAndPassword(email, password)
            val user = result.user ?: error("Kein Benutzer nach Registrierung")
            user.toAuthUser()
        }

    override suspend fun sendPasswordResetEmail(email: String): Result<Unit> =
        runCatching {
            auth.sendPasswordResetEmail(email.trim())
        }

    private fun FirebaseUser.toAuthUser(): AuthUser =
        AuthUser(
            uid = uid,
            email = email
        )

    override suspend fun getIdToken(): String? {
        val user = Firebase.auth.currentUser ?: return null
        return user.getIdTokenResult(forceRefresh = false).token
    }

    override suspend fun sendEmailVerification(): Result<Unit> = runCatching {
        val user = auth.currentUser ?: error("No user logged in")
        user.sendEmailVerification()
    }

    override suspend fun isEmailVerified(): Boolean {
        val user = auth.currentUser ?: return false
        user.reload()
        return user.isEmailVerified
    }
}